<template>
  <CBulkToolbar
    :areAllSelectedFavorites="areAllSelectedFavorites"
    :count="count"
    :more-actions="{
      manageFileAttributes: showBulkUpdateFileAttributes,
      manageFileDistributions: showBulkUpdateFileDistributions,
    }"
    :selectedCount="selectedItemIds.length"
    :shareboxDisabled="shareboxButtonDisabled"
    :shareboxTooltip="shareboxButtonTooltip"
    :showDelete="showDeleteButton"
    :showFavorite="showFavoriteButton"
    :showMore="showBulkUpdateFileAttributes || showBulkUpdateFileDistributions"
    :showMove="showMoveButton"
    :showSharebox="showShareboxButton"
    @addToSharebox="onAddToSharebox"
    @delete="onBulkDelete"
    @manageFileAttributes="manageFileAttributes"
    @manageFileDistributions="onBulkDistribution"
    @move="onMoveItems"
    @toggleFavorite="onToggleFavorite"
    @toggleSelect="toggleSelectAll"
  />
  <BulkShareboxWarningModal
    v-model:show="showShareboxWarningModal"
    :style="{ width: '640px' }"
    :unshareable-files="unshareableSelectedFiles"
    @confirm="addFilesToSharebox"
    @open-file="(fileId) => apiState.fileOpener?.({ fileId })"
  />
</template>

<script setup lang="ts">
import { computed, h, nextTick, inject, ref, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import useToast from '@lib/composables/useToast'
import useConfirmation from '@lib/composables/useConfirmation'

import useAppStore from '@browser/stores/app'
import useApiStore from '@browser/stores/api'

import useCanvasesManagementBulk from '@lib/components/canvases/CanvasesManagementBulk.use.tsx'
import CBulkToolbar from '@lib/components/CBulkToolbar/CBulkToolbar.vue'
import CBulkDeleteErrors, {
  type CBulkDeleteErrorDetails,
} from '@lib/components/CBulkDeleteErrors/CBulkDeleteErrors.vue'
import BulkShareboxWarningModal from './BulkShareboxWarningModal.vue'
import useAppEmit from '@browser/composables/useAppEmit'

import type { FavoriteCreator } from '@browser/stores/api'
import { isPitcherWkWebView } from '@lib/util/userAgents'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { GetInstanceMetadataTemplatesPayload } from '@sdk/payload.types'
import { type PaginatedMetadataTemplateList } from '@openapi'

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const metadataFetcher = inject<
  (params?: GetInstanceMetadataTemplatesPayload) => Promise<PaginatedMetadataTemplateList>
>('instanceMetadataTemplatesFetcher')

const isBulkUpdating = ref(false)
const showShareboxWarningModal = ref(false)

const { t } = useI18n()
const { toast } = useToast()
const confirmation = useConfirmation()
const isItemInSharebox = inject<(id: string) => boolean>('isItemInSharebox', () => false)
const { bulkUpdateFileAttributes, bulkUpdateFileDistributionDialog } = useCanvasesManagementBulk()
const {
  selectedItemIds,
  setCurrentState,
  selectedSection,
  resetSelectedItems,
  toggleSelectAll,
  showFilteredResults,
  appName,
  displayedFilesFolders,
  displayedFavorites,
  displayedRecentFiles,
  displayedFiles,
  enabledFavorites,
} = useAppStore()
const { state: apiState } = useApiStore()
const { appEmit } = useAppEmit()

const showBulkUpdateFileAttributes = computed(
  () =>
    appName.value === 'admin' &&
    selectedSection.value === 'all' &&
    !!launchDarkly.value.enable_bulk_update_file_attributes &&
    !!selectedItemIdsInFiles.value?.length,
)
const showMoveButton = computed(() => selectedSection.value === 'all' && appName.value === 'admin')
const showDeleteButton = computed(() => selectedSection.value === 'all' && appName.value === 'admin')
const showFavoriteButton = !isPitcherWkWebView && enabledFavorites.value //Note: Remove when iOS offline support is added
const selectedItemIdsInFiles = computed(() =>
  selectedItemIds.value.filter((id) => displayedFiles.value.some((file) => file.id === id)),
)
const selectedFileNames = computed(() =>
  displayedFiles.value.filter((file) => selectedItemIdsInFiles.value.includes(file.id)).map((file) => file.name ?? ''),
)

const showBulkUpdateFileDistributions = computed(
  () => appName.value === 'admin' && selectedSection.value === 'all' && !!selectedItemIdsInFiles.value?.length,
)

const areAllSelectedFavorites = computed(() => selectedItemIds.value.every((id) => !!apiState.favorites[id]))
const count = computed(() => {
  switch (selectedSection.value) {
    case 'all':
      return displayedFilesFolders.value.length
    case 'favorites':
      return displayedFavorites.value.length
    case 'recent':
      return displayedRecentFiles.value.length
    default:
      return 0
  }
})

const isBulkFilteringEnabled = computed(() => !!launchDarkly.value?.enable_sharebox_bulk_filtering)

const selectedFiles = computed(() => displayedFiles.value.filter((file) => selectedItemIds.value.includes(file.id)))

const unshareableSelectedFiles = computed(() =>
  selectedFiles.value.filter((file) => file.permissions?.can_share !== true),
)

const alreadyInShareboxFiles = computed(() => selectedFiles.value.filter((file) => isItemInSharebox(file.id)))

const shareableNotInShareboxFiles = computed(() =>
  selectedFiles.value.filter((file) => file.permissions?.can_share === true && !isItemInSharebox(file.id)),
)

const shareableFiles = computed(() => selectedFiles.value.filter((file) => file.permissions?.can_share === true))

const shareboxButtonDisabled = computed(() => {
  if (selectedFiles.value.length === 0) return true
  if (!isBulkFilteringEnabled.value) return false
  return shareableNotInShareboxFiles.value.length === 0
})

const shareboxButtonTooltip = computed(() => {
  if (!isBulkFilteringEnabled.value || !shareboxButtonDisabled.value) {
    return t('canvasUI.browserApp.contextMenu.addToSharebox')
  }

  const hasUnshareable = unshareableSelectedFiles.value.length > 0
  const hasAlreadyInSharebox = alreadyInShareboxFiles.value.length > 0

  if (hasUnshareable && hasAlreadyInSharebox) {
    return t('canvasUI.browserApp.bulkAddToSharebox.mixedIssues')
  } else if (hasAlreadyInSharebox) {
    return t('canvasUI.browserApp.bulkAddToSharebox.alreadyInSharebox')
  } else if (hasUnshareable) {
    return t('canvasUI.browserApp.bulkAddToSharebox.someFilesUnshareable')
  }
  return undefined
})

const showShareboxButton = computed(
  () => appName.value === 'impact' && !!launchDarkly.value?.enable_sharebox && selectedFiles.value.length > 0,
)

async function onToggleFavorite() {
  if (areAllSelectedFavorites.value) {
    const favoritesIds = selectedItemIds.value.map((selectedId) => {
      return apiState.favorites[selectedId].id
    })

    apiState.favoritesDeleter?.({ ids: favoritesIds }).then(({ deleted_ids, errors }) => {
      if (deleted_ids.length > 0) {
        toast.success(
          t('canvasUI.browserApp.content.bulkFavoritesRemoveDialog.successMsg', {
            count: deleted_ids.length,
          }),
        )
      }

      if (errors.length > 0) {
        const favoriteLookup = new Map(displayedFavorites.value.map((f) => [f.id, f.name]))
        const errorsDetails: CBulkDeleteErrorDetails[] = errors.map((error) => ({
          id: error.id,
          name: favoriteLookup.get(error.id) || '-',
          error_message: error.error_message,
        }))
        showBulkDeleteErrorDialog(errorsDetails, {
          title: t('canvasUI.browserApp.content.bulkFavoritesRemoveErrorDialog.title', {
            count: errorsDetails.length,
          }),
          confirmText: t('canvasUI.browserApp.content.bulkFavoritesRemoveErrorDialog.close'),
        })
      }
    })
  } else {
    const result = await Promise.allSettled(
      selectedItemIds.value
        .filter((id) => !apiState.favorites[id]) // omit already favorited
        .reduce((acc, id) => {
          const folderItem = apiState.folders.find((f) => f.id === id)
          const fileItem =
            displayedFiles.value.find((f) => f.id === id) ||
            Object.values(apiState.recent || {}).find((f) => f.id === id)
          if (folderItem || fileItem) {
            acc.push(
              apiState.favoriteCreator!({
                [folderItem ? 'folder_id' : 'file_id']: folderItem?.id ?? fileItem?.id,
              }),
            )
          }

          return acc
        }, [] as ReturnType<FavoriteCreator>[]),
    )

    result.some((r) => r.status !== 'fulfilled') && console.warn('could not create all favorites: ', result)
  }
  resetSelectedItems()
}

async function manageFileAttributes() {
  const selectedRows = ref(
    selectedItemIdsInFiles.value.reduce(
      (acc, id) => {
        acc[id] = true
        return acc
      },
      {} as Record<string, boolean>,
    ),
  )

  await bulkUpdateFileAttributes({
    selectedRows,
    fileNames: selectedFileNames.value,
    metadataFetcher,
    isBulkUpdating,
    onComplete: () => {
      if (apiState.folderItemsFetcher) apiState.folderItemsFetcher(apiState.currentFolderId ?? undefined)
      if (apiState.selectedFile?.id) apiState.fileFetcher?.(apiState.selectedFile.id)
      if (showFilteredResults.value) apiState.fileListFetcher?.()
    },
  })
}

async function onBulkDistribution() {
  const selectedRows = ref(
    selectedItemIdsInFiles.value.reduce(
      (acc, id) => {
        acc[id] = true
        return acc
      },
      {} as Record<string, boolean>,
    ),
  )

  await bulkUpdateFileDistributionDialog({
    selectedCount: selectedItemIdsInFiles.value.length,
    isBulkUpdating,
    selectedRows,
    onComplete: async () => {
      if (apiState.folderItemsFetcher) await apiState.folderItemsFetcher(apiState.currentFolderId ?? undefined)
      if (apiState.selectedFile?.id) await apiState.fileFetcher?.(apiState.selectedFile.id)
      if (showFilteredResults.value) await apiState.fileListFetcher?.()
    },
  })
}

function onMoveItems() {
  setCurrentState('batch-move')
}

async function showBulkDeleteErrorDialog(
  errorsDetails: CBulkDeleteErrorDetails[],
  { title, confirmText }: { title: string; confirmText: string },
) {
  await nextTick()
  confirmation.warning({
    style: { width: 'auto' },
    hasExit: false,
    hasCancel: false,
    title,
    content: () =>
      h(CBulkDeleteErrors, {
        errorsDetails: errorsDetails,
      }),
    confirmText,
  })
}

async function onBulkDelete() {
  confirmation.danger({
    hasExit: false,
    title: t('canvasUI.browserApp.content.bulkFileFoldersDeleteDialog.title'),
    content: t('canvasUI.browserApp.content.bulkFileFoldersDeleteDialog.content', {
      count: selectedItemIds.value.length,
    }),
    confirmText: t('canvasUI.browserApp.content.bulkFileFoldersDeleteDialog.confirm'),
    cancelText: t('canvasUI.browserApp.content.bulkFileFoldersDeleteDialog.cancel'),
    onConfirm: async () => {
      apiState.filesFoldersDeleter?.({ ids: selectedItemIds.value }).then(({ deleted_ids, errors }) => {
        if (deleted_ids.length > 0) {
          toast.success(
            t('canvasUI.browserApp.content.bulkFileFoldersDeleteDialog.successMsg', {
              count: deleted_ids.length,
            }),
          )
        }

        if (errors.length > 0) {
          const fileFolderLookup = new Map(displayedFilesFolders.value.map((f) => [f.id, f.name]))
          const errorsDetails: CBulkDeleteErrorDetails[] = errors.map((error) => ({
            id: error.id,
            name: fileFolderLookup.get(error.id) || '-',
            error_message: error.error_message,
          }))
          showBulkDeleteErrorDialog(errorsDetails, {
            title: t('canvasUI.browserApp.content.bulkFileFoldersDeleteErrorDialog.title', {
              count: errorsDetails.length,
            }),
            confirmText: t('canvasUI.browserApp.content.bulkFileFoldersDeleteErrorDialog.close'),
          })
        }
      })
    },
  })
}

function onAddToSharebox() {
  if (!isBulkFilteringEnabled.value) {
    shareableFiles.value.forEach((file) => {
      appEmit('addToSharebox', {
        id: file.id,
        fileId: file.id,
        name: file.name ?? '',
        type: 'file',
        thumbnailUrl: file.thumbnail_url ?? undefined,
      })
    })
    resetSelectedItems()
    return
  }

  const unshareableFiles = unshareableSelectedFiles.value
  const filesToAdd = shareableNotInShareboxFiles.value

  if (unshareableFiles.length > 0 && filesToAdd.length > 0) {
    showShareboxWarningModal.value = true
  } else if (filesToAdd.length > 0) {
    addFilesToSharebox()
  }
}

function addFilesToSharebox() {
  const filesToAdd = shareableNotInShareboxFiles.value
  filesToAdd.forEach((file) => {
    appEmit('addToSharebox', {
      id: file.id,
      fileId: file.id,
      name: file.name ?? '',
      type: 'file',
      thumbnailUrl: file.thumbnail_url ?? undefined,
    })
  })
  if (filesToAdd.length > 0) {
    toast.success(t('canvasUI.browserApp.contextMenu.contentAddedToSharebox'))
  }
  resetSelectedItems()
}
</script>
